import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { ResponsePromptVariant } from '@innermate/shared';
import { type FormEvent, useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  variant: ResponsePromptVariant | null;
  isPending: boolean;
  /** undefined = 변경 안 함, null = NULL 로 리셋. */
  onSubmit: (input: {
    label?: string | null;
    temperature?: number | null;
  }) => void;
}

/**
 * Variant 의 label / 기본 temperature 만 수정. modelMatch 는 변경 불가
 * (versions 이 그 키 기준으로 묶여있어 변경하면 데이터 의미 깨짐).
 */
export function VariantRenameModal({ open, onClose, variant, isPending, onSubmit }: Props) {
  const [label, setLabel] = useState('');
  const [temperature, setTemperature] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: 모달 열림/대상 변경 시점에만 시드.
  useEffect(() => {
    if (open && variant) {
      setLabel(variant.label ?? '');
      setTemperature(variant.temperature == null ? '' : String(variant.temperature));
    }
  }, [open, variant?.id]);

  if (!variant) return null;

  const isDefault = variant.modelMatch === '';
  const tempStr = temperature.trim();
  const tempNum = tempStr === '' ? undefined : Number(tempStr);
  const tempInvalid =
    tempStr !== '' &&
    (tempNum === undefined || !Number.isFinite(tempNum) || tempNum < 0 || tempNum > 2);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending || tempInvalid) return;
    // 변경된 필드만 보냄. label/temperature 빈 문자열 → null 로 reset.
    const labelTrim = label.trim();
    const labelPatch: string | null | undefined = isDefault
      ? undefined // 기본 variant 는 label 변경 의미 없음 — 보내지 않음
      : labelTrim === (variant.label ?? '')
        ? undefined
        : labelTrim === ''
          ? null
          : labelTrim;
    const currentTemp = variant.temperature == null ? '' : String(variant.temperature);
    const temperaturePatch: number | null | undefined =
      tempStr === currentTemp ? undefined : tempStr === '' ? null : tempNum;
    if (labelPatch === undefined && temperaturePatch === undefined) {
      onClose();
      return;
    }
    onSubmit({
      ...(labelPatch !== undefined ? { label: labelPatch } : {}),
      ...(temperaturePatch !== undefined ? { temperature: temperaturePatch } : {}),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Variant 설정 수정"
      description={
        isDefault
          ? '기본 variant 의 temperature 만 수정할 수 있습니다 (이름은 항상 "기본").'
          : `${variant.modelMatch} variant 의 표시명·기본 temperature 를 수정합니다.`
      }
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button type="submit" form="rp-variant-rename-form" disabled={isPending || tempInvalid}>
            {isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      }
    >
      <form id="rp-variant-rename-form" onSubmit={handleSubmit} className="space-y-3">
        {!isDefault ? (
          <div>
            <label
              className="block text-xs text-foreground-muted mb-1"
              htmlFor="rp-variant-edit-label"
            >
              표시명
            </label>
            <Input
              id="rp-variant-edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={`비워두면 모델 식별자 (${variant.modelMatch}) 그대로 표시`}
              maxLength={80}
              autoFocus
            />
          </div>
        ) : null}
        <div>
          <label
            className="block text-xs text-foreground-muted mb-1"
            htmlFor="rp-variant-edit-temp"
          >
            기본 temperature (0~2)
          </label>
          <Input
            id="rp-variant-edit-temp"
            type="number"
            min={0}
            max={2}
            step={0.01}
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            placeholder="비워두면 모델/엔드포인트 기본값"
            aria-invalid={tempInvalid}
            autoFocus={isDefault}
          />
          {tempInvalid ? (
            <div className="mt-1 text-xs text-destructive">0 과 2 사이 숫자여야 합니다.</div>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
